<script setup lang="ts">
import CustomerHospitalFiles from "@/customer/components/hospitals/customer-files/CustomerHospitalFiles.vue";
import Customer from "@/customer/interfaces/Customer";
import useInfiniteLoading from "@/core/composables/swr/useInfiniteLoading";
import {consumerHospitalApi, sharingApi} from "@/container";
import HospitalTimeline from "@/hospital/components/timeline/HospitalTimeline.vue";
import HospitalMessageInput from "@/hospital/components/hospital-message/HospitalMessageInput.vue";
import {computed, ref, watch} from "vue";
import {Sharing} from "@/core/interfaces/Sharing";
import ConsumerSharingItem from "@/hospital/components/sharing/ConsumerSharingItem.vue";
import SharingDetailsDialog from "@/hospital/components/sharing/SharingDetailsDialog.vue";
import useSwr from "@/core/composables/swr/useSwr";
import {cacheKey} from "@/lib/cache";
import SharingService from "@/sharing/services/SharingService";
import {MessageSquare} from "@lucide/vue";
import SharingSelect from "@/hospital/components/sharing/sharing-select/SharingSelect.vue";

const props = defineProps<{
    customer: Customer
}>();

const {
    data,
    isLoading,
    isLoadingMore,
    error,
    total,
    loadMore,
    refresh,
    hasMore
} = useInfiniteLoading(
    `consumers/${props.customer.consumer_key}/hospital-timeline`,
    (page, limit) => consumerHospitalApi().consumerTimeline(props.customer.consumer_key, page, limit),
    {limit: 5}
);

const recipient = ref<Sharing | undefined>(undefined);

const {data: sharings, isLoading: isLoadingSharings, mutate: mutateSharings} = useSwr(
    computed(() => cacheKey('sharings/ownerships', props.customer.consumer_key)),
    () => sharingApi().indexAsOwner({
        consumer_key: props.customer.consumer_key
    }),
);

watch(sharings, (newSharings) => {
    if (newSharings && newSharings.length > 0) {
        recipient.value = newSharings[0];
    } else {
        recipient.value = undefined;
    }
}, {immediate: true});

const displayedSharings = computed(() => {
    return (sharings.value ?? []).sort(SharingService.sortSharings);
});

const message = ref<string>("");

/**
 * Send the message.
 */
const sendMessage = () => {
    if (!recipient.value || !message.value.trim()) return;

    consumerHospitalApi().sendMessageToHospital(
        props.customer.consumer_key,
        message.value,
        [recipient.value.id]
    ).then(() => {
        message.value = "";

        return refresh();
    });
};
</script>

<template>
  <div class="container mx-auto space-y-8 pt-8">
    <div>
      <h2>Partages de données</h2>
      <div v-if="!isLoadingSharings">
        <SharingDetailsDialog
          v-for="sharing in (displayedSharings ?? [])" :key="sharing.id" :sharing="sharing"
          @sharing-updated="mutateSharings()"
        >
          <ConsumerSharingItem :sharing="sharing" />
        </SharingDetailsDialog>
        <p v-if="!displayedSharings?.length">
          Aucun partage de données trouvé pour ce patient.
        </p>
      </div>
    </div>
    <CustomerHospitalFiles :customer="customer" :sharings="sharings ?? []" @uploaded="refresh()" />

    <div class="space-y-4">
      <div v-if="sharings?.length" class="flex flex-col bg-white shadow sm rounded-lg p-6">
        <div class="flex gap-2 mb-2">
          <MessageSquare />
          <h3>Envoyer un message</h3>
        </div>
        <div class="flex items-center gap-2 pb-2">
          <p>
            Envoyer à :
          </p>
          <div class="self-end">
            <SharingSelect :sharings="sharings" :model-value="recipient" />
          </div>
        </div>
        <HospitalMessageInput v-model:model-value="message" @submit="sendMessage" />
      </div>

      <HospitalTimeline
        :loading="isLoading"
        :data="data"
        :total="total"
        :error="error?.message"
        :load-more="loadMore"
        :has-more="hasMore"
        :loading-more="isLoadingMore"
        @load-more="loadMore"
      />
    </div>
  </div>
</template>

<style scoped>

</style>
